import { enqueueSnackbar } from 'notistack';
import { create } from 'zustand';

import { authRepository } from 'src/auth/auth.repository';
import { setSession, clearSession, getStoredTokens } from 'src/auth/utils';
import { useUserStore } from 'src/stores/user/user.store';

type States = {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
};

type Actions = {
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  refreshAccessToken: () => Promise<string | null>;
  verifyRegister: (phone: string) => Promise<boolean>;
  sendLoginCode: (phone: string) => Promise<void>;
  login: (phone: string, code: string) => Promise<any>;
  register: (name: string, cpf: string, phone: string, termsVersionAccepted: string, sellerId?: string) => Promise<any>;
  logout: () => void;
};

type AuthStore = States & Actions;

const initialState: States = {
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),

  setLoading: (isLoading) => set({ isLoading }),

  verifyRegister: async (phone: string) => {
    try {
      set({ isLoading: true, error: null });
      const response = await authRepository.verifyRegister(phone);
      set({ isLoading: false });
      return response.exists;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao verificar registro';
      set({ isLoading: false, error: errorMessage });
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
      throw error;
    }
  },

  sendLoginCode: async (phone: string) => {
    try {
      set({ isLoading: true, error: null });
      await authRepository.sendLoginCode(phone);
      set({ isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao enviar código de login';
      set({ isLoading: false, error: errorMessage });
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
      throw error;
    }
  },

  login: async (phone: string, code: string) => {
    try {
      set({ isLoading: true, error: null });

      const authData = await authRepository.login({ phone, code });
      // Definir sessão e estado de autenticação
      setSession(authData.accessToken, authData.refreshToken);

      set({
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return authData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro no login';
      set({
        isLoading: false,
        error: errorMessage,
      });
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
      throw error;
    }
  },

  register: async (name: string, cpf: string, phone: string, termId: string, sellerId?: string) => {
    try {
      set({ isLoading: true, error: null });

      const authData = await authRepository.register({ name, cpf, phone, termId, sellerId });
      // Definir sessão e estado de autenticação
      set({
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });

      enqueueSnackbar('Conta criada com sucesso!', {
        variant: 'success',
      });
      await get().sendLoginCode(phone);
      return authData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro no registro';
      set({
        isLoading: false,
        error: errorMessage,
      });
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
      throw error;
    }
  },

  refreshAccessToken: async () => {
    const { refreshToken } = getStoredTokens();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await authRepository.refreshAccessToken(refreshToken);

      // Atualiza a sessão com ambos os tokens (novo accessToken e refreshToken)
      setSession(response.accessToken, response.refreshToken);

      return response.accessToken;
    } catch (error) {
      // Se falhar, limpa a sessão
      setSession(null, null);
      throw error;
    }
  },

  logout: async () => {
    await authRepository.logout();
    clearSession();
    set({
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });

    // Limpar dados do usuário quando fizer logout
    const userStore = useUserStore.getState();
    userStore.clearUser();
  },
}));
